// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import { Maybe } from 'types/UtilityTypes';
import { GenericSuggestion } from 'types/suggestions/CoreSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';

import { BatchLTLPersistedState, BatchLTLShipmentInput } from '../types';
import { sortBatchLTLSuggestionsBySourceOrder } from './getBatchLTLSuggestions';

type RestoredShipments = {
  shipments: BatchLTLShipmentInput[];
  editedFields: Record<number, string[]>;
};

const DATE_FIELDS = [
  'pickupDate',
  'pickupCloseDate',
  'deliveryDate',
  'deliveryCloseDate',
] as const;

// New defaults for everything the rep did not touch; the rep's saved value for every field
// they did. Shipments the save does not know start from defaults, and shipments the rep
// added come back whole after the email's shipments.
export function restoreBatchLTLShipments(
  defaults: BatchLTLShipmentInput[],
  saved: Maybe<BatchLTLPersistedState>
): RestoredShipments {
  if (!saved) {
    return { shipments: defaults, editedFields: {} };
  }

  const editedFields: Record<number, string[]> = {};
  const shipments = defaults.map((shipment) => {
    const { key } = shipment;
    const savedShipment = saved.shipments.find(
      (candidate) => candidate.key === key
    );
    const paths = saved.editedFields[key] ?? [];
    if (!savedShipment || paths.length === 0) {
      return shipment;
    }

    editedFields[key] = paths;
    const restored = _.cloneDeep(shipment);
    paths.forEach((path) => _.set(restored, path, _.get(savedShipment, path)));

    return reviveDates(restored);
  });
  const addedShipments = saved.shipments
    .filter(({ key }) => isAddedShipmentKey(key))
    .map(reviveDates);

  return { shipments: [...shipments, ...addedShipments], editedFields };
}

// Added shipments have negative keys; the email's shipments use their quote request id.
export function isAddedShipmentKey(key: number): boolean {
  return key < 0;
}

// Paths (relative to the shipment) where the rep's values differ from the email's
// defaults. Arrays that changed length count as one edit of the whole array.
export function getEditedFieldPaths(
  defaults: BatchLTLShipmentInput[],
  shipments: BatchLTLShipmentInput[]
): Record<number, string[]> {
  return Object.fromEntries(
    shipments
      .map((shipment): [number, string[]] => {
        const base = defaults.find(({ key }) => key === shipment.key);

        return [shipment.key, base ? getChangedPaths(base, shipment) : []];
      })
      .filter(([, paths]) => paths.length > 0)
  );
}

// Saved batch shipments still worth showing: those in the rep's reply and those Kit still
// lists as open. Kit drops quote requests handled elsewhere, so those drop out here too.
export function getRestorableBatchSuggestions(
  saved: Maybe<BatchLTLPersistedState>,
  curSuggestionList: GenericSuggestion[]
): SuggestedQuoteChange[] {
  if (!saved) {
    return [];
  }

  return sortBatchLTLSuggestionsBySourceOrder(
    saved.suggestions.filter((suggestion) => {
      const current = curSuggestionList.find(({ id }) => id === suggestion.id);

      return (
        saved.repliedQrIds.includes(suggestion.id) ||
        saved.acceptedQrIds.includes(suggestion.id) ||
        Boolean(
          current && isQuoteRequestActionable(current as SuggestedQuoteChange)
        )
      );
    })
  );
}

function getChangedPaths(
  base: unknown,
  current: unknown,
  prefix = ''
): string[] {
  if (_.isEqual(base, current)) {
    return [];
  }

  const isBranch = (value: unknown) =>
    _.isPlainObject(value) || Array.isArray(value);
  const hasSameShape =
    isBranch(base) &&
    isBranch(current) &&
    Array.isArray(base) === Array.isArray(current) &&
    (!Array.isArray(base) || base.length === (current as unknown[]).length);

  if (!hasSameShape) {
    return prefix ? [prefix] : [];
  }

  const keys = _.union(
    Object.keys(base as object),
    Object.keys(current as object)
  );

  return keys.flatMap((key) =>
    getChangedPaths(
      (base as Record<string, unknown>)[key],
      (current as Record<string, unknown>)[key],
      prefix ? `${prefix}.${key}` : key
    )
  );
}

// Saved state is JSON, so its dates come back as strings.
function reviveDates(shipment: BatchLTLShipmentInput): BatchLTLShipmentInput {
  const revived = { ...shipment };
  DATE_FIELDS.forEach((field) => {
    const value = revived[field] as unknown;
    if (typeof value === 'string') {
      (revived as Record<string, unknown>)[field] = new Date(value);
    }
  });

  return revived;
}
